import { useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  Input,
  Tag,
  Textarea,
} from "@diametral/design-system/react";

import { useSetStreamRoles, useTrackStream } from "../../api/projects";
import type { PersonBrief, Stream, StreamRoleKind, StreamStatus, Weather } from "../../api/types";
import { PeopleListField } from "../../components/PersonPicker";
import { RoleList, StatusSignal, WeatherSignal, statusLabel, weatherLabel } from "../../components/Pilot";
import { PlainText } from "../../components/PlainText";
import { SelectField } from "../../components/SelectField";
import { formatDate } from "../../lib/format";
import { t, useTranslation } from "../../lib/i18n";
import { notifySuccess } from "../../lib/toast";

const STATUSES: StreamStatus[] = ["not_started", "in_progress", "blocked", "done"];
const WEATHERS: Weather[] = ["green", "orange", "red"];

export function roleLabel(kind: StreamRoleKind): string {
  if (kind === "sponsor") return t("Sponsor");
  if (kind === "owner") return t("Owner");
  if (kind === "product_owner") return t("PO");
  return t("Tech lead");
}

/** The steering block of a stream card: status, weather, roles, dates, blocking point. */
export function StreamPilotSummary({ stream }: { stream: Stream }) {
  const { t } = useTranslation();
  const late = stream.launch_date && stream.launch_date < new Date().toISOString().slice(0, 10) && stream.status !== "done";
  return (
    <div className="ada-stack ada-stack--tight">
      <span className="ada-cluster">
        <StatusSignal status={stream.status} />
        <WeatherSignal weather={stream.weather} />
        {stream.complete ? null : <Tag tone="warning">{t("PO + TL missing")}</Tag>}
      </span>
      <div>
        {(["sponsor", "owner", "product_owner", "tech_lead"] as StreamRoleKind[]).map((k) =>
          k === "product_owner" || k === "tech_lead" || stream.roles[k].length ? (
            <RoleList key={k} label={roleLabel(k)} people={stream.roles[k]} />
          ) : null,
        )}
      </div>
      <span className="ada-cluster ada-faint">
        {stream.launch_date ? (
          <span className={late ? "ada-pilot-gap" : undefined}>{t("Launch: {{date}}", { date: formatDate(stream.launch_date) })}</span>
        ) : null}
        <span>
          {stream.next_passage ? t("Next committee: {{date}}", { date: formatDate(stream.next_passage) }) : t("No committee slot planned")}
        </span>
        <span>{stream.tracked_at ? t("Updated on {{date}}", { date: formatDate(stream.tracked_at) }) : t("Never updated")}</span>
      </span>
      {stream.pain_point ? (
        <div>
          <div className="ada-kicker">{t("Blocking point, comments")}</div>
          <PlainText text={stream.pain_point} className="ada-muted" />
        </div>
      ) : null}
    </div>
  );
}

export function TrackingDialog({ slug, stream, onClose }: { slug: string; stream: Stream; onClose: () => void }) {
  const { t } = useTranslation();
  const save = useTrackStream(slug);
  const [status, setStatus] = useState(stream.status ?? "");
  const [weather, setWeather] = useState(stream.weather ?? "");
  const [launch, setLaunch] = useState(stream.launch_date ?? "");
  const [target, setTarget] = useState(stream.target_ipc_date ?? "");
  const [pain, setPain] = useState(stream.pain_point);
  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Steer “{{title}}”", { title: stream.title })}</DialogTitle>
          <DialogDescription>{t("What the DAR sees on its dashboard. Every change is dated.")}</DialogDescription>
        </DialogHeader>
        <div className="ada-stack">
          <SelectField
            label={t("Status")}
            value={status}
            onChange={setStatus}
            options={[{ value: "", label: t("No status") }, ...STATUSES.map((s) => ({ value: s, label: statusLabel(s) }))]}
          />
          <SelectField
            label={t("Weather")}
            value={weather}
            onChange={setWeather}
            options={[{ value: "", label: t("Not given") }, ...WEATHERS.map((w) => ({ value: w, label: weatherLabel(w) }))]}
          />
          <Field>
            <FieldLabel htmlFor="track-launch">{t("Planned launch")}</FieldLabel>
            <Input id="track-launch" type="date" value={launch} onChange={(e) => setLaunch(e.target.value)} />
          </Field>
          <Field>
            <FieldLabel htmlFor="track-target">{t("Committee date wanted")}</FieldLabel>
            <Input id="track-target" type="date" value={target} onChange={(e) => setTarget(e.target.value)} />
          </Field>
          <Field>
            <FieldLabel htmlFor="track-pain">{t("Blocking point, comments")}</FieldLabel>
            <Textarea id="track-pain" rows={4} value={pain} onChange={(e) => setPain(e.target.value)} />
          </Field>
        </div>
        <DialogFooter>
          <Button
            disabled={save.isPending}
            onClick={() =>
              save.mutate(
                {
                  id: stream.id,
                  status: (status || null) as StreamStatus | null,
                  weather: (weather || null) as Weather | null,
                  launch_date: launch || null,
                  target_ipc_date: target || null,
                  pain_point: pain,
                },
                { onSuccess: () => (notifySuccess(t("Stream updated")), onClose()) },
              )
            }
          >
            {t("Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function StreamRolesDialog({ slug, stream, onClose }: { slug: string; stream: Stream; onClose: () => void }) {
  const { t } = useTranslation();
  const save = useSetStreamRoles(slug);
  const [roles, setRoles] = useState<Record<StreamRoleKind, PersonBrief[]>>(stream.roles);
  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Roles of “{{title}}”", { title: stream.title })}</DialogTitle>
          <DialogDescription>{t("A stream is complete with a PO and a tech lead. The DAR appoints them.")}</DialogDescription>
        </DialogHeader>
        <div className="ada-stack">
          {(["sponsor", "owner", "product_owner", "tech_lead"] as StreamRoleKind[]).map((k) => (
            <Field key={k}>
              <FieldLabel htmlFor={`role-${k}`}>{roleLabel(k)}</FieldLabel>
              <PeopleListField id={`role-${k}`} label={roleLabel(k)} value={roles[k]} onChange={(people) => setRoles((r) => ({ ...r, [k]: people }))} />
            </Field>
          ))}
        </div>
        <DialogFooter>
          <Button
            disabled={save.isPending}
            onClick={() =>
              save.mutate(
                {
                  id: stream.id,
                  sponsor: roles.sponsor.map((p) => p.id),
                  owner: roles.owner.map((p) => p.id),
                  product_owner: roles.product_owner.map((p) => p.id),
                  tech_lead: roles.tech_lead.map((p) => p.id),
                },
                { onSuccess: () => (notifySuccess(t("Roles saved")), onClose()) },
              )
            }
          >
            {t("Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
